import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, Alert } from '@storable/canaveral-components';
import includes from 'lodash/includes';
import { ADMIN_LANGUAGE_NAME_MAPPING, LOCALE_PILL_VALUES, EN_US } from '../../../src/shared/i18n';
import useFlipper from '../../../modules/react/hooks/useFlipper';
import { FLIPPER_INTERNATIONALIZATION } from '../../../constants/flippers';
import Button from '../../../modules/react/components/stateless/Button';
import Icon from '../../../modules/react/components/stateless/Icon';
import PageListDescendantPages from '../../containers/PageListDescendantPages';
import styles from './styles.scss';

const DEFAULT_DELETE_TEXT = `
  By deleting this page you are permanently removing it from the builder.
  Content and customizations on deleted pages cannot be recovered. Please proceed
  with caution.
`;

const I18N_DELETE_TEXT = `
  Deleting the EN-US version of this page will also delete all other language
  pages associated with it. Content and customizations on deleted pages cannot
  be recovered. Please proceed with caution.
`;

const propTypes = {
  onDismiss: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  page: PropTypes.shape({
    id: PropTypes.string.isRequired,
    children: PropTypes.arrayOf(
      PropTypes.shape({
        pageId: PropTypes.string.isRequired,
        locale: PropTypes.string.isRequired,
      }),
    ),
  }).isRequired,
  loading: PropTypes.bool.isRequired,
  submitErrors: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.number.isRequired,
      message: PropTypes.string.isRequired,
    }).isRequired,
  ),
  pagesDeleted: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
    }).isRequired,
  ),
};
const defaultProps = {
  submitErrors: [],
  pagesDeleted: [],
};

const PageDeleteModal = ({
  onDismiss,
  onSubmit,
  page,
  loading,
  submitErrors,
  pagesDeleted,
}) => {
  const [confirmed, setConfirmed] = useState(false);
  const [deleteDisabled, setDisabled] = useState(true);
  const i18nEnabled = useFlipper(FLIPPER_INTERNATIONALIZATION);
  const [pagesToDelete, setPagesToDelete] = useState(i18nEnabled ? [] : [page]);

  useEffect(() => {
    if (submitErrors.length) { setConfirmed(false); }
  }, [submitErrors]);

  useEffect(() => {
    if (pagesDeleted.length) { setConfirmed(true); }
  }, [pagesDeleted]);

  const onDelete = () => {
    onSubmit(pagesToDelete);
  };

  const handleDeleteDisabled = (value) => {
    setDisabled(value);
  };

  const languageCheckboxChange = (pageToDelete) => {
    const itemIndex = pagesToDelete.indexOf(pageToDelete);

    if (itemIndex < 0) {
      if (pageToDelete.locale === EN_US) {
        setPagesToDelete([pageToDelete, ...pageToDelete.children]);
      } else {
        setPagesToDelete(pages => [...pages, pageToDelete]);
      }
    } else if (pageToDelete.locale === EN_US) {
      setPagesToDelete([]);
    } else {
      setPagesToDelete(pages => pages.filter(existingPage => existingPage !== pageToDelete));
    }

    setDisabled(pagesToDelete.length > 0);
  };

  const descendantPagesWarning = (
    <PageListDescendantPages
      ids={pagesToDelete.map(pageToDelete => pageToDelete.id || pageToDelete.pageId)}
      message="Are you sure you want to delete this page?"
      onLoad={handleDeleteDisabled}
    />
  );

  const warning = (
    <Alert context="warning" className={styles.alert}>
      <span className={styles.icon}>
        <Icon label="delete-page-warning" id="warning-triangle" />
      </span>
      <span>{DEFAULT_DELETE_TEXT}</span>
    </Alert>
  );

  const i18nWarning = (
    <Alert context="warning" className={styles.alert}>
      <span className={styles.icon}>
        <Icon label="delete-page-warning" id="warning-triangle" />
      </span>
      <span>{I18N_DELETE_TEXT}</span>
    </Alert>
  );

  const languageCheckboxes = (
    <React.Fragment>
      <p className={styles.message}>
        Which languages of this page would you like to delete?
      </p>
      <p>
        <label>
          <input
            type="checkbox"
            value={page.id}
            checked={includes(pagesToDelete, page)}
            onChange={() => languageCheckboxChange(page)}
          />
          {' '}
          {LOCALE_PILL_VALUES[page.locale]} ({ADMIN_LANGUAGE_NAME_MAPPING[page.locale]})
        </label>
      </p>
      {
        page.children.map(childPage => (
          <p key={childPage.pageId}>
            <label>
              <input
                type="checkbox"
                value={childPage.pageId}
                checked={includes(pagesToDelete, childPage)}
                disabled={includes(pagesToDelete, page)}
                onChange={() => languageCheckboxChange(childPage)}
              />
              {' '}
              {LOCALE_PILL_VALUES[childPage.locale]}
              {' '}
              ({ADMIN_LANGUAGE_NAME_MAPPING[childPage.locale]})
            </label>
          </p>
        ))
      }
    </React.Fragment>
  );

  const deletePageBody = {
    message: i18nEnabled ? i18nWarning : warning,
    body: descendantPagesWarning,
    languages: i18nEnabled ? languageCheckboxes : null,
  };

  return (
    <Modal
      show
      onDismiss={onDismiss}
      aria-labelledby="page-delete-modal"
    >
      <Modal.Header>
        <Modal.Title>Delete Page</Modal.Title>
      </Modal.Header>
      <Modal.Body className={styles.message}>
        {!!submitErrors.length && !confirmed && (
          <Alert context="danger">
            {submitErrors.map(({ message }) => message)}
          </Alert>
        )}
        {confirmed
          ? (
            <Alert context="success">
              <span>Page deleted successfully!</span>
            </Alert>
          ) : (
            <React.Fragment>
              {deletePageBody.message}
              {deletePageBody.languages}
              {deletePageBody.body}
            </React.Fragment>
          )}
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={onDismiss} disabled={loading}>
          {confirmed ? 'Close' : 'Cancel'}
        </Button>
        {!confirmed
          && (
            <Button variant="danger" onClick={onDelete} disabled={loading || !!deleteDisabled}>
              Delete
            </Button>
          )
        }
      </Modal.Footer>
    </Modal>
  );
};

PageDeleteModal.propTypes = propTypes;
PageDeleteModal.defaultProps = defaultProps;

export default PageDeleteModal;
